import type { DriverPermissions } from './manifest.ts';

/**
 * The permission gate (proposal §6.3): the one implementation, in TypeScript, of what a driver may
 * reach. The driver host checks every `fetch`, `ws` and `tcp` of a driver with it before calling the
 * bridge, and the test harness checks with it too, so a driver that reaches an undeclared host
 * fails in its own tests first.
 *
 * Hosts are compared in the WHATWG URL parser's form (lower case, IPv4 in dotted decimal, IPv6 in
 * brackets, no trailing dot), and the request goes out with that form, so what was checked is what
 * is reached. Names are never resolved: `192.168.1.0/24` does not admit a name that resolves into it.
 * Limit: redirects are followed by the bridge and not checked here (see the C4 worklog).
 */

declare const URL: new (url: string) => { readonly href: string; readonly protocol: string; readonly hostname: string; readonly port: string; readonly pathname: string; readonly search: string; readonly hash: string; readonly username: string };

export type GateResult = { ok: true; value: string } | { ok: false; reason: string };

export interface PermissionGate {
    /** An `http:` or `https:` URL; `value` is the URL to request. */
    checkHttp(url: string) : GateResult;
    /** A `ws:` or `wss:` URL; `value` is the URL to open. */
    checkWs(url: string) : GateResult;
    /** `value` is the host to connect to (an IPv6 address without brackets). */
    checkTcp(host: string, port: number) : GateResult;
    /** What the driver may reach, for logs and the consent screen. */
    readonly allowed: readonly string[];
}

const HOST_CHARACTERS = /^[A-Za-z0-9._:[\]-]+$/;
const IPV4 = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/;
const IPV4_RANGE = /^(\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3})\/(\d{1,2})$/;
const SCHEME_PREFIX = /^[A-Za-z][A-Za-z0-9+.-]*:\/\//;
const MIN_PREFIX_LENGTH = 8;

/** A host in the URL parser's form, or undefined if it is not a plain host. */
export function canonicalHost(raw: string) : string | undefined {
    let host = raw.trim();
    if (host === '' || !HOST_CHARACTERS.test(host)) {
        return undefined;
    }
    if (host.includes(':') && !host.startsWith('[')) {
        host = `[${host}]`;
    }

    let parsed;
    try {
        parsed = new URL(`http://${host}/`);
    } catch {
        return undefined;
    }
    if (parsed.port !== '' || parsed.pathname !== '/' || parsed.search !== '' || parsed.hash !== '' || parsed.username !== '') {
        return undefined;
    }

    const name = parsed.hostname.endsWith('.') ? parsed.hostname.slice(0, -1) : parsed.hostname;

    return name === '' ? undefined : name;
}

/** The host a configuration value names: a host, `host:port`, `[v6]:port` or a URL. */
export function hostOfConfigValue(text: string) : string | undefined {
    const value = text.trim();
    if (SCHEME_PREFIX.test(value)) {
        try {
            return canonicalHost(new URL(value).hostname);
        } catch {
            return undefined;
        }
    }

    const bracketed = /^\[([^\]]+)\](?::\d{1,5})?$/.exec(value);
    if (bracketed) {
        return canonicalHost(`[${bracketed[1]}]`);
    }

    const parts = value.split(':');
    if (parts.length === 2 && /^\d{1,5}$/.test(parts[1] ?? '')) {
        return canonicalHost(parts[0] ?? '');
    }

    return canonicalHost(value);
}

function ipv4ToNumber(host: string) : number | undefined {
    const match = IPV4.exec(host);
    if (!match) {
        return undefined;
    }

    const octets = match.slice(1).map(Number);
    if (octets.some((octet) => octet > 255)) {
        return undefined;
    }

    return octets.reduce((sum, octet) => sum * 256 + octet, 0);
}

type Matcher = (host: string) => boolean;

/** Why a `permissions.hosts` entry is refused, or undefined when it is fine. */
export function checkHostPattern(pattern: string) : string | undefined {
    return compilePattern(pattern).problem;
}

function compilePattern(pattern: string) : { matcher: Matcher; problem?: undefined } | { matcher?: undefined; problem: string } {
    const text = pattern.trim();
    if (text === '*' || text === '*.*') {
        return { problem: 'allows every host' };
    }

    const range = IPV4_RANGE.exec(text);
    if (range) {
        const base = ipv4ToNumber(range[1] ?? '');
        const length = Number(range[2]);
        if (base === undefined || length > 32) {
            return { problem: 'is not an IPv4 range' };
        }
        if (length < MIN_PREFIX_LENGTH) {
            return { problem: `is wider than /${MIN_PREFIX_LENGTH}` };
        }

        const size = 2 ** (32 - length);
        const start = Math.floor(base / size) * size;

        return {
            matcher: (host) => {
                const address = ipv4ToNumber(host);

                return address !== undefined && address >= start && address < start + size;
            },
        };
    }

    if (text.startsWith('*.')) {
        const suffix = canonicalHost(text.slice(2));
        if (suffix === undefined || suffix.startsWith('[') || ipv4ToNumber(suffix) !== undefined) {
            return { problem: 'is not a host name after *.' };
        }

        return { matcher: (host) => host.endsWith(`.${suffix}`) && ipv4ToNumber(host) === undefined };
    }

    const host = canonicalHost(text);
    if (host === undefined) {
        return { problem: 'is not a host name or address' };
    }

    return { matcher: (candidate) => candidate === host };
}

function configHosts(permissions: DriverPermissions, config: Readonly<Record<string, unknown>>) : string[] {
    const hosts: string[] = [];
    for (const key of permissions.configHosts) {
        const value = config[key];
        const texts = typeof value === 'string' ? [value] : Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];
        for (const text of texts) {
            const host = hostOfConfigValue(text);
            if (host !== undefined) {
                hosts.push(host);
            }
        }
    }

    return hosts;
}

/** The gate for one driver instance: its manifest's permissions with its configuration. */
export function createPermissionGate(permissions: DriverPermissions, config: Readonly<Record<string, unknown>>) : PermissionGate {
    const matchers: Matcher[] = [];
    for (const pattern of permissions.hosts) {
        const compiled = compilePattern(pattern);
        // A manifest with a refused pattern is not installed; one that slips through allows nothing.
        if (compiled.matcher) {
            matchers.push(compiled.matcher);
        }
    }
    const fromConfig = configHosts(permissions, config);
    for (const host of fromConfig) {
        matchers.push((candidate) => candidate === host);
    }
    const allowed = [...permissions.hosts, ...fromConfig];

    const isAllowed = (host: string) : boolean => matchers.some((matches) => matches(host));
    const refuseHost = (host: string) : GateResult => ({
        ok: false,
        reason: `not permitted: ${host} is not among the hosts this driver may reach (${allowed.join(', ') || 'none'})`,
    });

    const checkUrl = (url: string, schemes: readonly string[]) : GateResult => {
        let parsed;
        try {
            parsed = new URL(url);
        } catch {
            return { ok: false, reason: 'not a valid URL' };
        }
        if (!schemes.includes(parsed.protocol)) {
            return { ok: false, reason: `not permitted: ${parsed.protocol} URLs (only ${schemes.join(' ')})` };
        }

        const host = canonicalHost(parsed.hostname);
        if (host === undefined) {
            return { ok: false, reason: 'not a valid URL' };
        }

        return isAllowed(host) ? { ok: true, value: parsed.href } : refuseHost(host);
    };

    return {
        checkHttp: (url) => checkUrl(url, ['http:', 'https:']),
        checkWs(url) {
            if (!permissions.ws) {
                return { ok: false, reason: 'not permitted: this driver may not open WebSocket connections' };
            }

            return checkUrl(url, ['ws:', 'wss:']);
        },
        checkTcp(rawHost, port) {
            if (!permissions.tcp) {
                return { ok: false, reason: 'not permitted: this driver may not open TCP connections' };
            }
            if (!Number.isInteger(port) || port < 1 || port > 65_535) {
                return { ok: false, reason: `not a valid port: ${port}` };
            }

            const host = canonicalHost(rawHost);
            if (host === undefined) {
                return { ok: false, reason: `not a valid host: ${rawHost}` };
            }
            if (!isAllowed(host)) {
                return refuseHost(host);
            }

            return { ok: true, value: host.startsWith('[') ? host.slice(1, -1) : host };
        },
        allowed,
    };
}
